import VueFriendlyIframe from 'vue-friendly-iframe';
import ChatbotMenu from './components/menu/ChatbotMenu';
import Chatbot from './components/common/Chatbot';
import CompactNudge from './components/common/CompactNudge';
import ChatbotService from './services/chatbot-service';
import chatbotStore from './stores/chatbot-store';
import BlackburnComponents from 'blackburn-components';

/**
 * Returns an initialized chatbot service.
 * @param {Object} ctx - Must include access to the vuex store.
 * @returns {Object} - Initialized chatbot service.
 */
export const initializeChatbotService = (ctx) => {
  const { store } = ctx;

  const chatbotService = new ChatbotService(ctx);
  store.registerModule('chatbotStore', chatbotStore);
  return chatbotService;
};

/**
 * @param {*} key store key
 * @param {*} storage storage used, localStorage in our case
 * @returns the correct key value store pair. Method will be used across
 * vuejs client apps in order to support backwards compatibility
 * with the new chatbot-core vuex store structure.
 */

export const getPersistedState = (key, storage) => {
  try {
    const storeValue = JSON.parse(storage[key]);
    const stack = storeValue.chatbotStore?.stack;
    const currentAmp = storeValue.chatbotStore?.amp;

    if (stack && currentAmp && stack.length >= 0) {
      return {
        ...storeValue,
        chatbotStore: {
          ...storeValue.chatbotStore,
          stack: { [currentAmp.id]: stack },
          ablyReady: false,
        },
      };
    }

    return { ...storeValue, chatbotStore: { ...storeValue.chatbotStore, ablyReady: false } };
  } catch (error) {
    return undefined;
  }
};

/**
 * Installs the chatbot component including all dependencies.
 */
export const ChatbotComponent = {
  install(Vue) {
    Vue.use(VueFriendlyIframe);
    Vue.use(BlackburnComponents);
    Vue.component('CompactNudge', CompactNudge);
    Vue.component('Chatbot', Chatbot);
    Vue.component('ChatbotMenu', ChatbotMenu);
  },
};

export const replaceStorageValue = (content, storage = {}, isRichText = false) => {
  const { key, value, prevValue } = storage;

  if (!key || typeof prevValue !== 'string') return content;

  let newValue = content;

  if (isRichText) {
    // Let's locate all storage value elements added via the quill-mention plugin
    const domValue = document.createElement('div');
    domValue.innerHTML = content;
    const mentions = domValue.getElementsByClassName('mention');

    // We will then replace all the quill-mention elements with their associated value
    Array.from(mentions).forEach((mention) => {
      const {
        dataset: { id: storageId },
      } = mention;

      // We only want to change the targeted storage items
      if (storageId !== key) return;

      const spanElem = Array.from(mention.children).find((child) => child.tagName.toLowerCase() === 'span');
      const textNode = spanElem && Array.from(spanElem.childNodes).find((child) => child.nodeName === '#text');
      if (textNode) {
        textNode.textContent = `${value}}}`;
        mention.dataset.value = `${value}}}`;
      }
    });

    newValue = domValue.innerHTML.toString();
  }

  const tokenReplaceRegex = new RegExp(`\\{\\{\\s?${prevValue}\\s?\\}\\}`, 'gm');

  return newValue.replace(tokenReplaceRegex, (fullMatch) =>
    prevValue ? fullMatch.replace(prevValue, value) : `{{${value}}}`
  );
};

/**
 * Installs a filter that can parse {{token}} to values from the storage on runtime.
 */
export const ParseTokenFilter = {
  install(Vue, ctx) {
    const { store } = ctx;
    Vue.config.productionTip = false;
    Vue.filter('parseTokens', (value) => {
      if (!value) {
        return '';
      }

      const isPreview = store.getters['chatbotStore/isPreview'];
      const isPreviewRunning = store.getters['chatbotStore/isPreviewRunning'];

      // When in preview and it isn't running, then we don't want to replace any storage values
      if (isPreview && !isPreviewRunning) {
        return value;
      }

      // Let's locate all storage value elements added via the quill-mention plugin
      const domValue = document.createElement('div');
      domValue.innerHTML = value;
      const mentions = domValue.getElementsByClassName('mention');

      // We will then replace all the quill-mention elements with their associated value
      Array.from(mentions).forEach((mention) => {
        const {
          dataset: { value: mentionValue },
        } = mention;
        // removing the trailing }} chars as we manually add those
        const storeValue = mentionValue?.replace(/}}$/, '');
        const storeToken = storeValue && store.getters['chatbotStore/getStorageValue'](storeValue);

        // If a storage value is found we will replace the token with the value,
        // otherwise we will delete the token from the ouput (unless we're in preview).
        if (storeToken) {
          const textNode = document.createTextNode(storeToken);
          mention.parentNode.replaceChild(textNode, mention);
        } else if (!isPreview) {
          mention.parentNode.removeChild(mention);
        }
      });

      return (
        domValue.innerHTML
          .toString()
          // Replacing any existing token strings e.g. {{test}}
          .replace(
            /\{\{([^"}]+)\}\}/gm,
            (fullMatch, token) =>
              store.getters['chatbotStore/getStorageValue'](token.trim()) ?? (isPreview ? fullMatch : '')
          )
      );
    });
  },
};
